import { ConditionalOptionValues, ConditionalStepField } from '@dill/dill-shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  buildValueEntry,
  deriveOptionState,
  duplicateWithNextGroupedPosition,
  isConditionalFieldVisible,
  OptionState,
} from './conditionalOptionUtils';

interface UseConditionalOptionParams {
  creditApplicationOptionData: Record<string, ConditionalStepField | null | undefined> | null;
  externalSelectedOption: string;
  externalFieldValues: ConditionalOptionValues[];
  staticPreview?: boolean;
  onChangeData: (data: { value: string; fieldValue: ConditionalOptionValues[] }) => void;
  // Answers from the rest of the form, keyed by field id — lets a nested field's
  // conditionalVisibility depend on a field outside this conditional.
  externalSourceValues?: Record<string, unknown>;
}

interface UseConditionalOptionResult {
  selectedOption: string;
  optionStates: Record<string, OptionState>;
  errors: { uniqueId: string; errorText: string }[];
  handleSelectOption: (key: string) => void;
  handleChangeValue: (args: { id: string; value: any; secondaryValue?: any }) => void;
  handleAddGroup: () => void;
  handleRemoveGroup: (args: { groupPosition: number }) => void;
  validate: () => boolean;
}

function buildAllOptionStates({
  creditApplicationOptionData,
  savedOption,
  savedValues,
  staticPreview,
}: {
  creditApplicationOptionData: Record<string, ConditionalStepField | null | undefined> | null;
  savedOption: string;
  savedValues: ConditionalOptionValues[];
  staticPreview: boolean;
}): Record<string, OptionState> {
  if (!creditApplicationOptionData) return {};
  const next: Record<string, OptionState> = {};
  for (const key in creditApplicationOptionData) {
    const rawDef = creditApplicationOptionData[key];
    if (!rawDef) continue;
    next[key] = deriveOptionState({
      optionKey: key,
      fieldDef: rawDef,
      savedValues: savedOption === key ? savedValues : [],
      existingValues: [],
      staticPreview,
    });
  }
  return next;
}

export function useConditionalOption({
  creditApplicationOptionData,
  externalSelectedOption,
  externalFieldValues,
  staticPreview = false,
  onChangeData,
  externalSourceValues,
}: UseConditionalOptionParams): UseConditionalOptionResult {
  // Keep onChangeData stable in a ref so handlers don't need it as a dep
  const onChangeDataRef = useRef(onChangeData);
  useEffect(() => {
    onChangeDataRef.current = onChangeData;
  }, [onChangeData]);

  // Keep creditApplicationOptionData in a ref for use inside effects without
  // causing extra re-runs when the object reference changes
  const optionDataRef = useRef(creditApplicationOptionData);
  optionDataRef.current = creditApplicationOptionData;

  // ── Initial state ────────────────────────────────────────────────────────
  const [selectedOption, setSelectedOption] = useState<string>(() => externalSelectedOption);
  const [optionStates, setOptionStates] = useState<Record<string, OptionState>>(() =>
    buildAllOptionStates({
      creditApplicationOptionData,
      savedOption: externalSelectedOption,
      savedValues: externalFieldValues,
      staticPreview,
    })
  );
  const [errors, setErrors] = useState<{ uniqueId: string; errorText: string }[]>([]);

  // ── Re-sync when external data arrives after initial mount ───────────────
  // Tracks what we last initialized from so we can detect genuinely new data
  // without re-initializing on every user-edit echo-back from the parent.
  const prevExtRef = useRef({
    option: externalSelectedOption,
    len: externalFieldValues.length,
  });

  useEffect(() => {
    const prev = prevExtRef.current;
    // New option selected from outside (e.g. draft loaded with a saved choice)
    const optionChanged = externalSelectedOption !== '' && externalSelectedOption !== prev.option;
    // Field values arrived for the first time (empty → populated)
    const dataArrived = prev.len === 0 && externalFieldValues.length > 0;

    prevExtRef.current = { option: externalSelectedOption, len: externalFieldValues.length };

    if (!optionChanged && !dataArrived) return;

    setOptionStates(
      buildAllOptionStates({
        creditApplicationOptionData: optionDataRef.current,
        savedOption: externalSelectedOption,
        savedValues: externalFieldValues,
        staticPreview,
      })
    );
    setSelectedOption(externalSelectedOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSelectedOption, externalFieldValues.length]);

  // ── User interaction handlers ────────────────────────────────────────────
  const handleSelectOption = useCallback(
    (key: string) => {
      const next = selectedOption === key ? '' : key;
      setSelectedOption(next);
      setErrors([]);
      onChangeDataRef.current({
        value: next,
        fieldValue: optionStates[next]?.values ?? [],
      });
    },
    [selectedOption, optionStates]
  );

  const handleChangeValue = useCallback(
    ({ id, value, secondaryValue }: { id: string; value: any; secondaryValue?: any }) => {
      // Read current state synchronously to avoid stale-closure on rapid edits
      setOptionStates((prev) => {
        const state = prev[selectedOption];
        if (!state) return prev;
        const newValues = state.values.map((v) =>
          v.uniqueId === id
            ? { ...v, value, ...(secondaryValue !== undefined ? { secondaryValue } : {}) }
            : v
        );
        const next = {
          ...prev,
          [selectedOption]: { ...state, values: newValues },
        };
        // Schedule the parent notification after the state update commits
        Promise.resolve().then(() => {
          onChangeDataRef.current({ value: selectedOption, fieldValue: newValues });
        });
        return next;
      });
      setErrors((prev) => prev.filter((e) => e.uniqueId !== id));
    },
    [selectedOption]
  );

  const handleAddGroup = useCallback(() => {
    setOptionStates((prev) => {
      const state = prev[selectedOption];
      if (!state) return prev;
      const newGroupedRows = duplicateWithNextGroupedPosition(state.groupedRows);
      const newMaxPos = Math.max(...newGroupedRows.map((r) => r.groupedPosition ?? 0));
      const added: ConditionalOptionValues[] = [];
      newGroupedRows
        .filter((r) => r.groupedPosition === newMaxPos)
        .forEach((row) => {
          row.fields.forEach((field) => {
            added.push(
              buildValueEntry({
                optionKey: selectedOption,
                fieldDef: state.fieldDef,
                field,
                groupedPosition: row.groupedPosition ?? 0,
                index: row.index ?? 0,
                groupColumnType: row.columnType ?? 'ONE_COLUMN',
                existingValues: [],
                savedValues: [],
              })
            );
          });
        });
      const newValues = [...state.values, ...added];
      const next = {
        ...prev,
        [selectedOption]: { ...state, groupedRows: newGroupedRows, values: newValues },
      };
      Promise.resolve().then(() => {
        onChangeDataRef.current({ value: selectedOption, fieldValue: newValues });
      });
      return next;
    });
  }, [selectedOption]);

  const handleRemoveGroup = useCallback(
    ({ groupPosition }: { groupPosition: number }) => {
      setOptionStates((prev) => {
        const state = prev[selectedOption];
        if (!state) return prev;
        const newValues = state.values.filter((v) => v.groupedPosition !== groupPosition);
        const next = {
          ...prev,
          [selectedOption]: {
            ...state,
            groupedRows: state.groupedRows.filter((r) => r.groupedPosition !== groupPosition),
            values: newValues,
          },
        };
        Promise.resolve().then(() => {
          onChangeDataRef.current({ value: selectedOption, fieldValue: newValues });
        });
        return next;
      });
      setErrors((prev) =>
        prev.filter((e) => {
          // We can't access optionStates inside the setState callback so we
          // read it from the closure — safe because errors are cleared on submit
          const val = optionStates[selectedOption]?.values.find((v) => v.uniqueId === e.uniqueId);
          return val?.groupedPosition !== groupPosition;
        })
      );
    },
    [selectedOption, optionStates]
  );

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = useCallback(() => {
    const currentValues = optionStates[selectedOption]?.values ?? [];
    const newErrors: { uniqueId: string; errorText: string }[] = [];

    currentValues.forEach((field) => {
      // Hidden by its "Show this field only if…" rule — nothing to validate.
      if (
        !isConditionalFieldVisible({
          field,
          optionValues: currentValues,
          externalSourceValues,
          groupedPosition: field.groupedPosition,
        })
      )
        return;
      if (field.isRequired) {
        let isEmpty = false;
        if (
          ['TEXT', 'TEXT_AREA', 'OPTION_FIELD'].includes(field.fromField) &&
          (!field.value || (typeof field.value === 'string' && field.value.trim() === ''))
        )
          isEmpty = true;
        if (
          ['NUMBER', 'CURRENCY'].includes(field.fromField) &&
          (field.value === null || field.value === '' || isNaN(Number(field.value)))
        )
          isEmpty = true;
        if (field.fromField === 'DATE' && !field.value) isEmpty = true;
        if (field.fromField === 'CHECK_BOX' && field.value !== true) isEmpty = true;
        if (field.fromField === 'FILE' && !field.value) isEmpty = true;
        if (field.fromField === 'CONDITIONAL_OPTION' && !field.value) isEmpty = true;
        if (field.fromField === 'STATE_DROPDOWN' && !field.value) isEmpty = true;
        if (isEmpty)
          newErrors.push({ uniqueId: field.uniqueId, errorText: `${field.name} is required` });
      }
      if (
        field.fromField === 'TEXT' &&
        field.isFieldEmail &&
        field.value &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.toString())
      ) {
        newErrors.push({
          uniqueId: field.uniqueId,
          errorText: `${field.name} must be a valid email.`,
        });
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  }, [selectedOption, optionStates, externalSourceValues]);

  return {
    selectedOption,
    optionStates,
    errors,
    handleSelectOption,
    handleChangeValue,
    handleAddGroup,
    handleRemoveGroup,
    validate,
  };
}
